import { definePlugin } from '../../framework';
import { EntryType } from '../../../../types/src/schemas';
import { LogType } from '../../types';
import type { UtilsApi } from '../../types';
import {
  detectTablesFromFullText,
  extractFieldsFromTableWithLLM,
  splitLines,
  type TableExtractResponse,
} from './tableLlmClient';
import {
  getChosenIterator,
  hasContainerTableIterator,
  readCurrentIterator,
  whenIteratorBootstrapped,
  type ChosenIterator,
} from '../iterators';
import { suggestionTypeFor } from '../../utils';
import { useGlobalPluginStore } from '../../store';

export enum Domain {
  Intermodal = 'intermodal',
  Retail = 'retail',
  EasyEOM = 'easyeom',
}

type DetectedTable = { startLine: number; endLine: number; reason?: string };

const TABLE_START_PAD = 3;
const TABLE_END_PAD = 3;

const MIN_HEADER_COLUMNS = 3;

/**
 * A column header: several columns, mostly without digits. Data rows
 * (container numbers, seals, weights, dates) mostly have them.
 */
export function looksLikeColumnHeader(line: string | undefined): boolean {
  const cells = columnSpans(line).map(({ start, end }) =>
    (line ?? '').slice(start, end),
  );
  if (cells.length < MIN_HEADER_COLUMNS) return false;
  return cells.filter((cell) => /\d/.test(cell)).length * 2 <= cells.length;
}

/**
 * Moves a column header that ends one table to the start of the next table
 * when that table begins right below it. Table detection sometimes cuts
 * between a header and its rows; the extraction then sees a header without
 * values in one table and values without a header in the other.
 */
function keepHeadersWithRows(
  tables: DetectedTable[],
  lines: string[],
): DetectedTable[] {
  const result = tables.map((t) => ({ ...t }));
  for (let i = 0; i < result.length - 1; i++) {
    const t = result[i];
    const next = result[i + 1];
    const closeBelow = next.startLine - t.endLine <= TABLE_END_PAD + 1;
    if (
      t.endLine > t.startLine &&
      closeBelow &&
      looksLikeColumnHeader(lines[t.endLine]) &&
      // A table that already starts with a header doesn't need another one;
      // the line is more likely a row of text ("ROTTERDAM   SHANGHAI").
      !startsWithHeader(next, lines) &&
      !leavesHeaderAlone(t, lines)
    ) {
      next.startLine = t.endLine;
      t.endLine -= 1;
    }
  }
  return result;
}

const nonBlankLines = (lines: string[], from: number, to: number) =>
  lines.slice(from, to + 1).filter((line) => line.trim() !== '');

function startsWithHeader(table: DetectedTable, lines: string[]): boolean {
  const [first] = nonBlankLines(lines, table.startLine, table.endLine);
  return looksLikeColumnHeader(first);
}

/** Whether moving a table's last line away would leave its header without rows. */
function leavesHeaderAlone(table: DetectedTable, lines: string[]): boolean {
  const rest = nonBlankLines(lines, table.startLine, table.endLine - 1);
  return rest.length > 0 && looksLikeColumnHeader(rest[rest.length - 1]);
}

/**
 * The last line a table's end padding may reach: a few lines down, but not
 * into the next table or the end of the document, and not onto a line that
 * looks like a column header, which belongs to the table below.
 */
function paddedEnd(t: DetectedTable, limit: number, lines: string[]): number {
  const furthest = Math.min(t.endLine + TABLE_END_PAD, limit);
  let end = t.endLine;
  while (end < furthest && !looksLikeColumnHeader(lines[end + 1])) end += 1;
  return end;
}

/**
 * Widens the line ranges the table detection returned. The model counts
 * lines itself and is regularly off by one, which can leave a table's last
 * line (often its only data row) outside every table. Starts move up and
 * ends move down by a few lines, without running into a neighbouring table
 * or past the end of the document. An end stops before a line that looks
 * like a column header, so a table found a few lines late keeps its header;
 * then a header that still ends a table moves to the table below (see
 * keepHeadersWithRows).
 */
export function padTableRanges(
  tables: DetectedTable[],
  lines: string[],
): DetectedTable[] {
  const lineCount = lines.length;
  let prevEnd = -1;
  const padded = tables.map((t, i) => {
    const next = tables[i + 1];
    const startLine = Math.max(prevEnd + 1, t.startLine - TABLE_START_PAD);
    const limit = Math.min(next ? next.startLine - 1 : Number.POSITIVE_INFINITY, lineCount - 1);
    const endLine = paddedEnd(t, limit, lines);
    prevEnd = endLine;
    return { ...t, startLine, endLine };
  });
  // After the padding: padding a table's end can pull in the header of the
  // table below it.
  return keepHeadersWithRows(padded, lines);
}

const MAX_COLUMNS_LINE = 3;

/**
 * The lines below a header to measure a table's columns on, best first.
 * Columns are normally measured on the header itself; when values are not
 * aligned with their header (centered, right-aligned, or two values under
 * one label), a line further down lines up with the values instead. The
 * line with the most columns is tried first; empty lines are skipped.
 */
export function rankColumnLines(lines: string[], header: string): number[] {
  const at = headerLineIndex(lines, header);
  if (at < 0) return [];
  return Array.from({ length: MAX_COLUMNS_LINE }, (_, i) => i + 1)
    .map((offset) => ({ offset, count: columnSpans(lines[at + offset]).length }))
    .filter(({ count }) => count > 0)
    .sort((a, b) => b.count - a.count || a.offset - b.offset)
    .map(({ offset }) => offset);
}

type Span = { start: number; end: number };

/**
 * The line holding a table's header label. A title above the table can
 * mention the label too ("Container Delivery Order"), so a line that looks
 * like a column header wins over the first line that contains the label.
 */
const headerLineIndex = (lines: string[], header: string) => {
  const label = header.trim();
  if (!label) return -1;
  const withLabel = lines.flatMap((line, i) => (line.includes(label) ? [i] : []));
  return withLabel.find((i) => looksLikeColumnHeader(lines[i])) ?? withLabel[0] ?? -1;
};

/** The columns of a line (text separated by 2+ spaces), with their positions. */
const columnSpans = (line: string | undefined): Span[] =>
  [...(line ?? '').matchAll(/\S+(?: \S+)*/g)].map((m) => ({
    start: m.index ?? 0,
    end: (m.index ?? 0) + m[0].length,
  }));

const overlap = (a: Span, b: Span) =>
  Math.max(0, Math.min(a.end, b.end) - Math.max(a.start, b.start));

/**
 * Whether column \`col\`, measured on the line \`columnsLine\` below the
 * header, lies under the header column holding the field's label. A lower
 * line can have the same number of columns as the header and still not line
 * up with it — for instance when one of its cells is empty, or two labels
 * share a header column — and then the cell would belong to a neighbour.
 */
export function columnFitsHeader(
  lines: string[],
  header: string,
  columnsLine: number,
  col: number,
): boolean {
  const at = headerLineIndex(lines, header);
  if (at < 0) return false;
  const cell = columnSpans(lines[at + columnsLine])[col];
  if (!cell) return false;

  const headerLine = lines[at];
  const headerColumns = columnSpans(headerLine);
  const labelStart = headerLine.indexOf(header.trim());
  const labelColumn = headerColumns.find(
    (span) => span.start <= labelStart && labelStart < span.end,
  );
  if (!labelColumn) return false;

  // The header column the cell overlaps most is the one it belongs to.
  const best = headerColumns.reduce((a, b) =>
    overlap(cell, b) > overlap(cell, a) ? b : a,
  );
  return overlap(cell, best) > 0 && best === labelColumn;
}

function resolveDomain(input?: Domain): Domain {
  switch (input) {
    case Domain.Intermodal:
      return Domain.Intermodal;
    case Domain.Retail:
      return Domain.Retail;
    case Domain.EasyEOM:
      return Domain.EasyEOM;
    default:
      return Domain.Intermodal;
  }
}

type DeclareItemsState = { domain?: Domain };

type StructuredOption = {
  value: string;
  row: number;
  col: number;
  header: string;
  confidence: unknown;
  repeating: boolean;
  verified?: boolean;
  tableIndex: number;
  expectedValue?: string;
  /** Line below the header the columns are measured on; 0 is the header. */
  columnsLine?: number;
};

type StructuredResult = Record<string, { options?: StructuredOption[] }>;
type FailureReason =
  | 'implausibleValue'
  | 'nonReusableAnchor'
  | 'anchorNotInDocument'
  | 'anchorNotInEverySection'
  | 'labelAfterValue'
  | 'renderMismatch';
type FailingField = { key: string; value: string; reason: FailureReason };

let cachedStructuredResult: StructuredResult | null = null;
let containersTableIndex: number | null = null;

let cachedBaseTemplate: StructuredResult | null = null;
let cachedPerFileScores: Record<number, Record<string, boolean>> = {};
let cachedFileTexts: string[] = [];
let processedFileTexts: Set<string> = new Set();

const MAX_FIELD_SUGGESTIONS = 3;


let fieldPaths = new Map<string, string[]>();
let documentText = '';
let documentLines: string[] = [];

function normalizeForAnchorSearch(text: string): string {
  return text.replace(/\s+/g, ' ');
}

export function anchorOccursIn(normalizedText: string, anchor: unknown): boolean {
  if (typeof anchor !== 'string' || !anchor.trim()) return false;
  if (!normalizedText) return true;
  return normalizedText.includes(normalizeForAnchorSearch(anchor.trim()));
}
let scoringIterator: ChosenIterator | null = null;
let iteratorSections: string[][] = [];

export function anchorInEverySection(sections: string[][], anchor: unknown): boolean {
  if (typeof anchor !== 'string' || !anchor.trim()) return false;
  if (sections.length === 0) return true;
  const needle = normalizeForAnchorSearch(anchor.trim());
  return sections.every((section) =>
    normalizeForAnchorSearch(section.join('\n')).includes(needle),
  );
}

const ITERATOR_WAIT_MS = 60_000;

async function waitForIteratorBootstrap() {
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    whenIteratorBootstrapped(),
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, ITERATOR_WAIT_MS);
    }),
  ]);
  clearTimeout(timer);
}

const FIELD_ENTRY_TYPES = [EntryType.Simple, EntryType.Graph, EntryType.Cell];

function collectFieldPaths(
  getPaths:
    | ((type?: EntryType) => { isOk(): boolean; value?: string[][] })
    | undefined,
): Map<string, string[]> {
  const byKey = new Map<string, string[]>();
  for (const type of FIELD_ENTRY_TYPES) {
    const result = getPaths?.(type);
    const paths = result?.isOk() ? (result.value ?? []) : [];
    for (const path of paths) {
      const key = path[path.length - 1]?.toLowerCase();
      if (key && !key.startsWith('#') && !byKey.has(key)) byKey.set(key, path);
    }
  }
  return byKey;
}

export function isUnderIterator(
  fieldPath: string[] | undefined,
  iterator: ChosenIterator | null,
): boolean {
  if (!fieldPath || !iterator) return false;
  const { mapPath } = iterator;
  return (
    fieldPath.length > mapPath.length &&
    mapPath.every((segment, i) => fieldPath[i] === segment)
  );
}

function iteratorForField(fieldKey: string): ChosenIterator | null {
  return isUnderIterator(fieldPaths.get(fieldKey.toLowerCase()), scoringIterator)
    ? scoringIterator
    : null;
}

function spansMultipleTables(options: { tableIndex: number }[]): boolean {
  return new Set(options.map((opt) => opt.tableIndex)).size > 1;
}

const ITERATOR_PROBE_KEY = '__declareItemsProbe';

export function wrapInIterator(iteratorValue: unknown, snippet: unknown) {
  return { '#iterator': iteratorValue, [ITERATOR_PROBE_KEY]: snippet };
}

export function extractProbeValues(rendered: unknown): unknown[] {
  const iterations = Array.isArray(rendered) ? rendered : [rendered];
  return iterations.map((iteration) =>
    iteration !== null && typeof iteration === 'object'
      ? (iteration as Record<string, unknown>)[ITERATOR_PROBE_KEY]
      : undefined,
  );
}

function hasProbe(rendered: unknown): boolean {
  const iterations = Array.isArray(rendered) ? rendered : [rendered];
  return iterations.some(
    (iteration) =>
      iteration !== null &&
      typeof iteration === 'object' &&
      ITERATOR_PROBE_KEY in iteration,
  );
}

function withIterator(
  render: UtilsApi['callRender'],
  iteratorValue: unknown,
): UtilsApi['callRender'] {
  return async (code) => {
    const wrapped = await render(
      JSON.stringify(wrapInIterator(iteratorValue, JSON.parse(code))),
    );
    if (wrapped.isOk() && hasProbe(wrapped.value)) {
      return wrapped.map(extractProbeValues);
    }

    return render(code);
  };
}

const getContainerPath = (getPaths?: () => any) =>
  getPaths?.()?.container?.path ?? ['root'];

const toStrPath = (path: (string | number)[]) => path.map(String);

function ensurePath(
  getValue: ((path: string[]) => any) | undefined,
  addKey: ((path: string[], type: EntryType, node: any) => any) | undefined,
  path: string[],
  rootName = 'Containers',
) {
  if (!getValue || !addKey) return;
  if (getValue(path) !== undefined) return;

  for (let i = 0; i < path.length; i++) {
    const seg = path.slice(0, i + 1);
    if (getValue(seg) === undefined) {
      const key = seg[seg.length - 1];
      addKey(seg, EntryType.Map, {
        key,
        name: i === 0 ? rootName : String(key),
      });
    }
  }
}

function buildFieldSemanticsSection(
  getPaths: (type?: EntryType) => any,
  getValue: (path: string[]) => any,
): string {
  const collectPaths = (type: EntryType): string[][] => {
    const result = getPaths(type);
    return result?.isOk?.() ? result.value : [];
  };

  const allPaths = [
    ...collectPaths(EntryType.Simple),
    ...collectPaths(EntryType.Graph),
    ...collectPaths(EntryType.Cell),
  ];

  const seenKeys = new Set<string>();
  const dedupedPaths = allPaths.filter((path) => {
    const key = path[path.length - 1];
    if (seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });

  const fieldLines = dedupedPaths.map((path) => {
    const fieldKey = path[path.length - 1];
    const nodeResult = getValue(path);
    const semantics = nodeResult?.isOk?.() ? nodeResult.value?.semantics : undefined;
    return semantics ? `- ${fieldKey} (${semantics})` : `- ${fieldKey}`;
  });

  if (fieldLines.length === 0) return '';

  return (
    `FIELD DESCRIPTIONS (use to identify the correct column/value for each key):\n` +
    fieldLines.join('\n')
  );
}

function determineContainersTableIndex(): number | null {
  const containerOptions = cachedStructuredResult?.['container']?.options ?? [];
  if (containerOptions.length === 0) return null;

  const counts = new Map<number, number>();
  for (const opt of containerOptions) {
    counts.set(opt.tableIndex, (counts.get(opt.tableIndex) ?? 0) + 1);
  }

  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
}

export const useTableLogic = definePlugin<DeclareItemsState>({
  name: 'Declare Items',
  version: '0.3.1',
  initialState: { domain: undefined },
  settings: {
    autofill: {
      name: 'Auto-fill suggestions',
      type: 'boolean',
      default: true,
    },
  },

  bootstrap: async ({
    getPaths,
    addKey,
    state,
    getValue,
    log,
    progress,
    extractWithLLM,
    callRender,
    createCallRender,
    ...ctx
  }) => {
    const containerPath = toStrPath(getContainerPath(getPaths));
    ensurePath(getValue as any, addKey as any, containerPath, 'Containers');

    log('[Bootstrap] DeclareItems LLM plugin...');
    progress(5, 'Starting');

    const textContent = (ctx as any)?.textContent as unknown;
    if (typeof textContent !== 'string' || !textContent.trim()) {
      log('[Bootstrap] No textContent found');
      progress(85, 'No text in document');
      return;
    }

    const { fileIndex, totalFiles } = useGlobalPluginStore.getState();
    const isFirstFile = fileIndex === 0;
    const scoredFileCount = Object.keys(cachedPerFileScores).length;
    const isNewSession = scoredFileCount === 0 || totalFiles !== scoredFileCount + 1;

    if (isFirstFile && isNewSession) {
      cachedBaseTemplate = null;
      cachedPerFileScores = {};
      cachedFileTexts = [];
      processedFileTexts = new Set();
    }

    if (processedFileTexts.has(textContent) && !isFirstFile) {
      cachedStructuredResult = cachedBaseTemplate
        ? JSON.parse(JSON.stringify(cachedBaseTemplate))
        : {};
      return;
    }

    cachedStructuredResult = {};
    containersTableIndex = null;
    documentText = normalizeForAnchorSearch(textContent);
    const domain = resolveDomain(state.domain);
    const fieldsSection = buildFieldSemanticsSection(getPaths as any, getValue as any);

    try {
      progress(10, 'Finding tables');
      let detectSim = 10;
      const detectTicker = setInterval(() => {
        detectSim = Math.min(25, detectSim + 0.3);
        progress(detectSim, 'Finding tables');
      }, 125);

      const lines = splitLines(textContent);
      documentLines = lines;
      const detected = await detectTablesFromFullText({
        textContent,
        extractWithLLM,
      });
      detected.tables = padTableRanges(detected.tables, lines);
      clearInterval(detectTicker);

      progress(28, `Reading ${detected.tables.length} table(s)`);
      let extractSim = 28;
      const extractTicker = setInterval(() => {
        extractSim = Math.min(50, extractSim + 0.3);
        progress(extractSim, `Reading ${detected.tables.length} table(s)`);
      }, 125);

      await processDetectedTables({
        lines,
        domain,
        detected: detected.tables,
        extractWithLLM,
        fieldsSection,
      });
      clearInterval(extractTicker);

      log(`[LLM] Done. Tables processed: ${detected.tables.length}`);
      progress(53, 'Checking values');

      fieldPaths = collectFieldPaths(getPaths as any);
      await waitForIteratorBootstrap();
      scoringIterator = readCurrentIterator(
        getPaths as any,
        getValue as any,
        getChosenIterator(),
      );
      const sectionAnchors = countOccurrenceAnchors(scoringIterator?.value);
      iteratorSections = sectionAnchors ? splitIntoSections(lines, sectionAnchors) : [];

      const failingFields = await scoreStructuredOptions(callRender);
      progress(65, `Retrying ${failingFields.length} field(s)`);
      await retryFailingFields({
        failingFields,
        detected: detected.tables,
        lines,
        domain,
        extractWithLLM,
        fieldsSection,
      });
      await rescoreRetriedFields(
        failingFields.map((f) => f.key),
        callRender,
      );

      progress(75, 'Reading repeating sections');
      await extractFromIteratorSections({
        lines,
        domain,
        extractWithLLM,
        fieldsSection,
        callRender,
      });

      processedFileTexts.add(textContent);
      await reconcileAcrossFiles({
        isFirstFile,
        fileIndex,
        rawFileResult: cachedStructuredResult,
        callRender,
        createCallRender,
        detected: detected.tables,
        lines,
        domain,
        extractWithLLM,
        fieldsSection,
        textContent,
      });

      progress(85, 'Declare Items complete');

      containersTableIndex = determineContainersTableIndex();
    } catch (e) {
    }
  },

  async fieldSuggestions({
    entry,
    settings,
    iteratorStrategy,
    callRender,
    renderInContext,
  }) {
    if (entry.key === '#iterator') return [];

    if (!cachedStructuredResult) {
      return [];
    }

    const normalize = (s: string) => s.toLowerCase().trim();
    const fieldKey = normalize(entry.key);

    const fieldData = cachedStructuredResult?.[fieldKey];
    if (
      !fieldData ||
      !Array.isArray(fieldData.options) ||
      fieldData.options.length === 0
    ) {
      return [];
    }

    const isTableIteratorActive =
      containersTableIndex !== null &&
      (hasContainerTableIterator() || iteratorStrategy === 'table');

    const hasActiveIterator =
      iteratorStrategy === 'table' ||
      iteratorStrategy === 'countOccurrence' ||
      hasContainerTableIterator();

    const forceRepeatingForField =
      iteratorForField(fieldKey) !== null ||
      (hasActiveIterator && spansMultipleTables(fieldData.options));

    const llmPairs = selectReusableOptions(fieldKey, fieldData.options)
      .map((opt: any) => {
        const value = String(opt.value ?? '').trim();
        const rawHeader = String(opt.header ?? '');
        const header = rawHeader.trim() ? rawHeader : '';
        const forcedRepeating = forceRepeatingForField;
        const repeating = forcedRepeating ? true : opt.repeating;
        const snippet = buildSnippetForOption({
          row: opt.row,
          col: opt.col,
          header,
          repeating,
          columnsLine: opt.columnsLine,
        });
        return {
          suggestion: {
            value,
            snippet: snippet as any,
            type: suggestionTypeFor(entry.type),
            autofill: settings['autofill'] === true,
            verified: opt.verified === true,
            expectedValue: opt.expectedValue,
          },
          meta: {
            source: 'llm',
            header,
            row: opt.row,
            col: opt.col,
            tableIndex: opt.tableIndex,
            llmRepeating: opt.repeating,
            forcedRepeating,
            confidence: opt.confidence,
          },
        };
      });

    const columnEligibleOptions = isTableIteratorActive
      ? fieldData.options.filter(
          (opt: any) => opt.tableIndex === containersTableIndex,
        )
      : [];

    const seenCols = new Set<number>();
    const columnPairs = columnEligibleOptions
      .filter(
        (opt: any) =>
          typeof opt.col === 'number' &&
          !seenCols.has(opt.col) &&
          seenCols.add(opt.col),
      )
      .map((opt: any) => ({
        suggestion: {
          value: `c${opt.col}: ${String(opt.value ?? '').trim()}`,
          snippet: `#>$column#${opt.col}`,
          type: EntryType.Code,
          autofill: false,
        },
        meta: {
          source: 'column',
          header: opt.header,
          row: opt.row,
          col: opt.col,
          tableIndex: opt.tableIndex,
          llmRepeating: opt.repeating,
          forcedRepeating: true,
          confidence: opt.confidence,
        },
      }));

    const allPairs = [...llmPairs, ...columnPairs];

    const iterator = iteratorForField(fieldKey);
    const render =
      renderInContext ??
      (iterator ? withIterator(callRender, iterator.value) : callRender);
    const keep = await Promise.all(
      allPairs.map(async ({ suggestion: s, meta }) => {
        if (meta.source !== 'llm') return true;
        const result = await render(JSON.stringify(s.snippet));
        const expected =
          ('expectedValue' in s ? s.expectedValue : undefined) ?? s.value ?? '';
        return result.isOk() && matchRender(expected, result.value).passes;
      }),
    );

    return allPairs.filter((_, i) => keep[i]).map((p) => p.suggestion);
  },
});

function normalizeForCompare(text: string): string {
  return text.replace(/\s+/g, ' ').trim().toLowerCase();
}

function stripEdgePunctuation(token: string): string {
  return token.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
}

function tokenize(text: string): string[] {
  return normalizeForCompare(text)
    .split(' ')
    .map(stripEdgePunctuation)
    .filter(Boolean);
}

function containsTokenSequence(anchorTokens: string[], valueTokens: string[]): boolean {
  if (valueTokens.length === 0) return false;
  for (let i = 0; i + valueTokens.length <= anchorTokens.length; i++) {
    if (valueTokens.every((token, offset) => anchorTokens[i + offset] === token)) {
      return true;
    }
  }
  return false;
}

function collectTableValues(tableIndex: number): Set<string> {
  const values = new Set<string>();
  if (!cachedStructuredResult) return values;

  for (const fieldData of Object.values(cachedStructuredResult)) {
    for (const opt of fieldData.options ?? []) {
      if (opt.tableIndex !== tableIndex) continue;
      const value = String(opt.value ?? '').trim();
      if (value) values.add(value);
    }
  }

  return values;
}

const MIN_ANCHOR_LENGTH = 2;

function isReusableAnchor(
  anchor: unknown,
  opt: { value: unknown; tableIndex: number },
): boolean {
  if (typeof anchor !== 'string') return false;
  const trimmedAnchor = anchor.trim();
  const anchorTokens = tokenize(trimmedAnchor);
  if (anchorTokens.length === 0) return false;
  if (anchorTokens.join('').length < MIN_ANCHOR_LENGTH) return false;

  const knownValues = collectTableValues(opt.tableIndex);
  const ownValue = String(opt.value ?? '').trim();
  if (ownValue) knownValues.add(ownValue);

  for (const value of knownValues) {
    if (containsTokenSequence(anchorTokens, tokenize(value))) return false;
  }

  return true;
}

const FIELD_VALUE_VALIDATORS: Record<string, (value: string) => boolean> = {
  container: (value) => !/^\d+(\s*\/\s*\d+)?$/.test(value.trim()),
};

function isPlausibleValue(fieldKey: string, value: unknown): boolean {
  if (typeof value !== 'string') return true;
  const validator = FIELD_VALUE_VALIDATORS[fieldKey.toLowerCase()];
  return validator ? validator(value) : true;
}

function isAmbiguousAnchor(
  anchor: unknown,
  opt: { value: unknown; tableIndex: number },
  ownFieldKey: string,
): boolean {
  if (typeof anchor !== 'string' || !cachedStructuredResult) return false;
  const anchorTokens = tokenize(anchor);
  if (anchorTokens.length === 0) return false;

  const ownValue = normalizeForCompare(String(opt.value ?? ''));

  for (const [fieldKey, fieldData] of Object.entries(cachedStructuredResult)) {
    if (fieldKey === ownFieldKey) continue;

    for (const other of fieldData.options ?? []) {
      if (other.tableIndex === opt.tableIndex) continue;
      if (typeof other.header !== 'string' || !other.header.trim()) continue;

      const otherValue = normalizeForCompare(String(other.value ?? ''));
      if (otherValue === ownValue) continue;

      const otherAnchorTokens = tokenize(other.header);
      if (
        containsTokenSequence(anchorTokens, otherAnchorTokens) ||
        containsTokenSequence(otherAnchorTokens, anchorTokens)
      ) {
        return true;
      }
    }
  }

  return false;
}

function selectBestOptions(fieldKey: string, options: StructuredOption[]): StructuredOption[] {
  return [...options]
    .sort((a, b) => {
      if (a.verified !== b.verified) return a.verified ? -1 : 1;

      const aPlausible = isPlausibleValue(fieldKey, a.value);
      const bPlausible = isPlausibleValue(fieldKey, b.value);
      if (aPlausible !== bPlausible) return aPlausible ? -1 : 1;

      const aReusable = hasUsableAnchor(fieldKey, a);
      const bReusable = hasUsableAnchor(fieldKey, b);
      if (aReusable !== bReusable) return aReusable ? -1 : 1;

      const aAmbiguous = isAmbiguousAnchor(a.header, a, fieldKey);
      const bAmbiguous = isAmbiguousAnchor(b.header, b, fieldKey);
      if (aAmbiguous !== bAmbiguous) return aAmbiguous ? 1 : -1;

      const aLen = typeof a.header === 'string' ? a.header.trim().length : Infinity;
      const bLen = typeof b.header === 'string' ? b.header.trim().length : Infinity;
      if (aLen !== bLen) return aLen - bLen;

      const ca = typeof a.confidence === 'number' ? a.confidence : 0;
      const cb = typeof b.confidence === 'number' ? b.confidence : 0;
      return cb - ca;
    })
    .slice(0, MAX_FIELD_SUGGESTIONS);
}

function hasUsableAnchor(fieldKey: string, opt: StructuredOption): boolean {
  const iterator = iteratorForField(fieldKey);
  return (
    (iterator !== null && isIteratorAnchor(iterator, opt.header)) ||
    isReusableAnchor(opt.header, opt)
  );
}

export function dedupeByCell<T extends { header: string; row: number; col: number }>(
  options: T[],
): T[] {
  const seen = new Set<string>();
  return options.filter((opt) => {
    const key = candidateKey(opt);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function selectReusableOptions(fieldKey: string, options: StructuredOption[]): StructuredOption[] {
  return selectBestOptions(fieldKey, dedupeByCell(options)).filter(
    (opt) => hasUsableAnchor(fieldKey, opt) && isPlausibleValue(fieldKey, opt.value),
  );
}

function buildSnippetForOption(opt: {
  row: number;
  col: number;
  header: string;
  repeating: boolean;
  columnsLine?: number;
}) {
  const cellKey = opt.repeating
    ? `#>${opt.row},${opt.col}$cell#`
    : `#${opt.row},${opt.col}$cell#`;
  return [
    [
      {
        [cellKey]: {
          from: opt.header,
          fromLine: 0,
          columns: {
            '#$composeTableColumns': {
              from: opt.header,
              lines: 1,
              fromLine: opt.columnsLine ?? 0,
              compose: false,
            },
          },
        },
      },
    ],
  ];
}

export function getReferenceOptions(): Array<{ value: string; snippet: unknown; verified: boolean }> {
  if (!cachedStructuredResult) return [];

  const options = cachedStructuredResult['clientreference']?.options ?? [];
  return options
    .filter((opt) => opt?.value)
    .map((opt) => ({
      value: String(opt.value).trim(),
      snippet: buildSnippetForOption({
        row: opt.row,
        col: opt.col,
        header: opt.header,
        repeating: opt.repeating,
        columnsLine: opt.columnsLine,
      }),
      verified: true,
    }));
}

function getPromptName(domain: Domain) {
  return domain === Domain.Retail
    ? 'Retail_Declare_Items'
    : 'Intermodal_Declare_Items';
}

async function scoreStructuredOptions(
  callRender: UtilsApi['callRender'],
): Promise<FailingField[]> {
  if (!cachedStructuredResult) {
    return [];
  }

  const scoringResults = await Promise.all(
    Object.entries(cachedStructuredResult).map(
      async ([fieldKey, fieldData]) => {
        const opts = fieldData.options ?? [];
        if (opts.length === 0) {
          return {
            fieldKey,
            passing: [],
            fallback: null,
            fallbackReason: undefined,
            rawTableIndexes: new Set<number>(),
            passingTableIndexes: new Set<number>(),
          };
        }

        const optResults = await Promise.all(
          opts.map((opt) => scoreOption(fieldKey, opt, callRender)),
        );

        const passing = optResults
          .filter((r) => r.passes)
          .map((r) => ({
            ...r.opt,
            value: r.rendered ?? r.opt.value,
            expectedValue: r.opt.value,
            verified: true,
          }));

        return {
          fieldKey,
          passing,
          fallback: opts[0] ?? null,
          fallbackReason: optResults[0]?.reason,
          rawTableIndexes: new Set(opts.map((o) => o.tableIndex)),
          passingTableIndexes: new Set(passing.map((o) => o.tableIndex)),
        };
      },
    ),
  );

  const failingFields: FailingField[] = [];
  for (const {
    fieldKey,
    passing,
    fallback,
    fallbackReason,
    rawTableIndexes,
    passingTableIndexes,
  } of scoringResults) {
    cachedStructuredResult[fieldKey] = { options: passing };

    const hasUncoveredOccurrence = [...rawTableIndexes].some(
      (idx) => !passingTableIndexes.has(idx),
    );

    if ((passing.length === 0 || hasUncoveredOccurrence) && fallback?.value) {
      failingFields.push({
        key: fieldKey,
        value: String(fallback.value).trim(),
        reason: fallbackReason ?? 'renderMismatch',
      });
    }
  }

  return failingFields;
}

async function rescoreRetriedFields(
  fieldKeys: string[],
  callRender: UtilsApi['callRender'],
) {
  if (!cachedStructuredResult || fieldKeys.length === 0) {
    return;
  }

  await Promise.all(
    fieldKeys.map(async (fieldKey) => {
      const opts = cachedStructuredResult![fieldKey]?.options ?? [];
      if (opts.length === 0) {
        return;
      }

      const results = await Promise.all(
        opts.map((opt) => scoreOption(fieldKey, opt, callRender)),
      );


      cachedStructuredResult![fieldKey] = {
        options: results
          .filter((r) => r.passes)
          .map((r) => ({
            ...r.opt,
            value: r.rendered ?? r.opt.value,
            expectedValue: r.opt.value,
            verified: true,
          })),
      };
    }),
  );
}

function findAnchorProblem(
  opt: StructuredOption,
  iterator: ChosenIterator | null,
): FailureReason | null {
  const onIteratorAnchor = iterator !== null && isIteratorAnchor(iterator, opt.header);

  if (!onIteratorAnchor && !isReusableAnchor(opt.header, opt)) {
    return 'nonReusableAnchor';
  }
  if (!anchorOccursIn(documentText, opt.header)) {
    return 'anchorNotInDocument';
  }
  return iterator ? findSectionAnchorProblem(opt, onIteratorAnchor) : null;
}

function findSectionAnchorProblem(
  opt: StructuredOption,
  onIteratorAnchor: boolean,
): FailureReason | null {
  if (!anchorInEverySection(iteratorSections, opt.header)) {
    return 'anchorNotInEverySection';
  }
  if (onIteratorAnchor) return null;
  return labelPrecedesValue(iteratorSections[0] ?? [], opt.header, opt.row, opt.col)
    ? null
    : 'labelAfterValue';
}

async function scoreOption(
  fieldKey: string,
  opt: StructuredOption,
  callRender: UtilsApi['callRender'],
): Promise<{
  opt: StructuredOption;
  passes: boolean;
  reason?: FailureReason;
  rendered?: string;
}> {
  if (!opt?.value || typeof opt.value !== 'string') {
    return { opt, passes: false, reason: 'renderMismatch' };
  }

  if (!isPlausibleValue(fieldKey, opt.value)) {
    return { opt, passes: false, reason: 'implausibleValue' };
  }

  const iterator = iteratorForField(fieldKey);
  const anchorProblem = findAnchorProblem(opt, iterator);
  if (anchorProblem) {
    return { opt, passes: false, reason: anchorProblem };
  }

  const render = iterator ? withIterator(callRender, iterator.value) : callRender;
  const renderedOpt = iterator ? { ...opt, repeating: true } : opt;

  const { found, directRendered } = await locateCell(opt, renderedOpt, render);
  return found
    ? { opt: found.opt, passes: true, rendered: found.rendered }
    : { opt, passes: false, reason: 'renderMismatch', rendered: directRendered };
}

/**
 * Finds the cell that renders an option's value, trying in order:
 *   1. the cell as proposed — but when it only matches part of the value,
 *      the header's column boundaries may cut through it, so the columns
 *      measured lower down are tried first for an exact match;
 *   2. a cell next to it;
 *   3. the columns measured on a line below the header.
 * A cell that renders the header label itself never counts.
 */
export async function locateCell(
  opt: StructuredOption,
  renderedOpt: StructuredOption,
  render: UtilsApi['callRender'],
): Promise<{
  found: { opt: StructuredOption; rendered: string } | null;
  directRendered?: string;
}> {
  const usable = (rendered: string) => !isAnchorLabel(rendered, opt.header);
  const onColumnsLine = (m: { col: number; columnsLine: number; rendered: string }) => ({
    opt: { ...opt, col: m.col, columnsLine: m.columnsLine },
    rendered: m.rendered,
  });

  const direct = await renderCell(renderedOpt, render);
  if (direct?.passes && usable(direct.rendered)) {
    const exact = isExactRender(opt.value, direct.rendered)
      ? null
      : await findColumnsLineMatch(renderedOpt, render, true);
    return {
      found:
        exact && usable(exact.rendered)
          ? onColumnsLine(exact)
          : { opt, rendered: direct.rendered },
    };
  }

  const nearby = await findNearbyMatch(renderedOpt, render);
  if (nearby && usable(nearby.rendered)) {
    return {
      found: { opt: { ...opt, row: nearby.row, col: nearby.col }, rendered: nearby.rendered },
    };
  }

  const shifted = await findColumnsLineMatch(renderedOpt, render);
  if (shifted && usable(shifted.rendered)) return { found: onColumnsLine(shifted) };

  return { found: null, directRendered: direct?.rendered };
}

const MIN_PARTIAL_MATCH_LENGTH = 3;

const COLUMN_GAP = /\s{3,}|\t/;

function countColumnSegments(text: string): number {
  return text.split(COLUMN_GAP).filter((s) => s.trim()).length;
}

export function valueMatchesRendered(expected: string, rendered: string): boolean {
  const expectedTrimmed = expected.trim();
  if (!rendered || !expectedTrimmed) return false;

  if (normalizeForCompare(rendered) === normalizeForCompare(expectedTrimmed)) {
    return true;
  }

  if (countColumnSegments(rendered) > countColumnSegments(expectedTrimmed)) {
    return false;
  }

  if (containsWhole(rendered, expectedTrimmed)) return true;

  if (
    rendered.length >= MIN_PARTIAL_MATCH_LENGTH &&
    containsWhole(expectedTrimmed, rendered)
  ) {
    return true;
  }

  return false;
}

const WORD_CHAR = /[\p{L}\p{N}]/u;

function containsWhole(haystack: string, needle: string): boolean {
  for (
    let i = haystack.indexOf(needle);
    i !== -1;
    i = haystack.indexOf(needle, i + 1)
  ) {
    const before = haystack[i - 1];
    const after = haystack[i + needle.length];
    const cutsBefore = before !== undefined && WORD_CHAR.test(before) && WORD_CHAR.test(needle[0]);
    const cutsAfter =
      after !== undefined && WORD_CHAR.test(after) && WORD_CHAR.test(needle[needle.length - 1]);
    if (!cutsBefore && !cutsAfter) return true;
  }
  return false;
}

const MIN_FILLED_OCCURRENCE_RATIO = 0.75;

const MIN_SAME_KIND_RATIO = 0.75;

type ValueKind = 'time' | 'number' | 'text';

export function isAnchorLabel(rendered: string, anchor: unknown): boolean {
  return (
    typeof anchor === 'string' &&
    normalizeForCompare(rendered) === normalizeForCompare(anchor)
  );
}

export function valueKind(value: string): ValueKind {
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(value.trim())) return 'time';
  return /\p{L}/u.test(value) ? 'text' : 'number';
}

export function matchRender(
  expected: string,
  value: unknown,
): { passes: boolean; rendered: string } {
  const asText = (v: unknown) => (v == null ? '' : String(v).trim());

  if (!Array.isArray(value)) {
    const rendered = asText(value);
    return { passes: valueMatchesRendered(expected, rendered), rendered };
  }

  const occurrences = value.flat(Infinity).map(asText);
  const filled = occurrences.filter(Boolean);
  const hit = filled.find((v) => valueMatchesRendered(expected, v));
  const mostlyFilled =
    filled.length >= occurrences.length * MIN_FILLED_OCCURRENCE_RATIO;
  const expectedColumns = countColumnSegments(expected.trim());
  const cutsAcrossCells = filled.some(
    (v) => countColumnSegments(v) > expectedColumns,
  );
  const hitKind = hit === undefined ? undefined : valueKind(hit);
  const consistentKind =
    filled.filter((v) => valueKind(v) === hitKind).length >=
    filled.length * MIN_SAME_KIND_RATIO;

  return {
    passes:
      hit !== undefined && mostlyFilled && !cutsAcrossCells && consistentKind,
    rendered: hit ?? JSON.stringify(occurrences),
  };
}

const NEARBY_SEARCH_RADIUS = 2;

const normaliseRender = (text: string) =>
  text.replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Whether a cell rendered to the whole expected value, not just part of it
 * (matchRender also accepts partial matches).
 */
export function isExactRender(expected: string, rendered: string): boolean {
  return normaliseRender(expected) === normaliseRender(rendered);
}

/**
 * Retries a cell with its columns measured on a line below the header (see
 * rankColumnLines), at the proposed column and the columns around it. Values
 * that are not aligned with their header are found this way.
 */
async function findColumnsLineMatch(
  opt: StructuredOption,
  callRender: UtilsApi['callRender'],
  exactOnly = false,
): Promise<{ col: number; columnsLine: number; rendered: string } | null> {
  const baseCol = typeof opt.col === 'number' ? opt.col : 0;
  const cols = [0, -1, 1, -2, 2]
    .slice(0, 1 + 2 * NEARBY_SEARCH_RADIUS)
    .map((d) => baseCol + d)
    .filter((col) => col >= 0);

  for (const columnsLine of rankColumnLines(documentLines, opt.header)) {
    for (const col of cols) {
      if (!columnFitsHeader(documentLines, opt.header, columnsLine, col)) {
        continue;
      }
      const match = await renderCell({ ...opt, col, columnsLine }, callRender);
      if (match?.passes && (!exactOnly || isExactRender(opt.value, match.rendered))) {
        return { col, columnsLine, rendered: match.rendered };
      }
    }
  }
  return null;
}

/** Renders an option's cell and compares it with the value it should hold. */
async function renderCell(
  opt: StructuredOption,
  callRender: UtilsApi['callRender'],
): Promise<{ passes: boolean; rendered: string } | null> {
  const renderResult = await callRender(JSON.stringify(buildSnippetForOption(opt)));
  return renderResult.isOk() ? matchRender(opt.value, renderResult.value) : null;
}

async function findNearbyMatch(
  opt: StructuredOption,
  callRender: UtilsApi['callRender'],
): Promise<{ row: number; col: number; rendered: string } | null> {
  if (opt.value.trim().length < MIN_PARTIAL_MATCH_LENGTH) return null;

  const baseRow = typeof opt.row === 'number' ? opt.row : 0;
  const baseCol = typeof opt.col === 'number' ? opt.col : 0;

  const candidates: { row: number; col: number }[] = [];
  for (let dRow = -NEARBY_SEARCH_RADIUS; dRow <= NEARBY_SEARCH_RADIUS; dRow++) {
    for (let dCol = -NEARBY_SEARCH_RADIUS; dCol <= NEARBY_SEARCH_RADIUS; dCol++) {
      if (dRow === 0 && dCol === 0) continue;
      const row = baseRow + dRow;
      const col = baseCol + dCol;
      if (row < 0 || col < 0) continue;
      candidates.push({ row, col });
    }
  }

  candidates.sort(
    (a, b) =>
      Math.abs(a.row - baseRow) + Math.abs(a.col - baseCol) -
      (Math.abs(b.row - baseRow) + Math.abs(b.col - baseCol)),
  );

  for (const { row, col } of candidates) {
    const match = await renderCell({ ...opt, row, col }, callRender);
    if (match?.passes) {
      return { row, col, rendered: match.rendered };
    }
  }

  return null;
}

function buildRetryInstruction(failingFields: FailingField[]) {
  const fieldLines = failingFields
    .map(({ key, value, reason }) =>
      reason === 'implausibleValue'
        ? `  - ${key}: the previous value "${value}" does not look right for this field (e.g. a count or sequence number instead of the actual identifier). Find the field's real value elsewhere in the table, plus its anchor.`
        : `  - ${key}: expected value "${value}" (this value is correct — only the anchor needs fixing)`,
    )
    .join('\n');

  return (
    `RETRY INSTRUCTION — READ THIS FIRST AND OVERRIDE ALL OTHER INSTRUCTIONS:\n` +
    `Extract ONLY the specific fields listed below.\n` +
    `Return "options": [] for every field NOT in this list.\n` +
    `For fields marked "this value is correct": the value is right, only the anchor was rejected — it was built from actual DATA in the table (e.g. a container number, reference code, quantity, or any other value from that same row) rather than from the table's HEADER row. Keep that value and find a genuine header/label anchor for it instead.\n` +
    `For fields marked with a value that "does not look right": do not keep reusing that value — re-examine the table and find the field's actual correct value.\n` +
    `A GOOD anchor is column header/label text that stays the same across different documents using this same template. It must be text that is ACTUALLY PRESENT, verbatim, in the table lines below — never invent, paraphrase, or guess a plausible-sounding label; copy it exactly as printed.\n` +
    `A BAD anchor is any text taken from a data row — even if it does not exactly match the value you are extracting, if it looks like a specific record's data rather than a stable column label, it is WRONG.\n` +
    `Find a genuine header/label anchor directly above or beside the value.\n` +
    `\n` +
    `Fields to retry:\n` +
    `${fieldLines}`
  );
}

async function retryFailingFields(args: {
  failingFields: FailingField[];
  detected: DetectedTable[];
  lines: string[];
  domain: Domain;
  extractWithLLM: UtilsApi['extractWithLLM'];
  fieldsSection: string;
  retryInstruction?: string;
}) {
  const { failingFields, detected, lines, domain, extractWithLLM, fieldsSection } = args;
  if (failingFields.length === 0) {
    return;
  }

  const retryInstruction = args.retryInstruction ?? buildRetryInstruction(failingFields);
  const failingKeys = new Set(failingFields.map((f) => f.key));
  const promptName = getPromptName(domain);
  const retryResults = await Promise.all(
    detected.map(async (t) => {
      const tableLines = lines.slice(t.startLine, t.endLine + 1);
      try {
        return await extractFieldsFromTableWithLLM({
          tableLines,
          promptName,
          extractWithLLM,
          retryInstruction,
          fieldsSection,
        });
      } catch {
        return null;
      }
    }),
  );

  retryResults.forEach((extracted, tableIndex) => {
    if (!extracted) {
      return;
    }

    for (const key of failingKeys) {
      mergeExtractedOptions(key, (extracted[key]?.options ?? []) as any[], tableIndex);
    }
  });
}

function retagTableIndex(
  option: StructuredOption,
  rawOptions?: StructuredOption[],
): StructuredOption {
  const match = rawOptions?.find((o) => o.header === option.header);
  return match ? { ...option, tableIndex: match.tableIndex } : option;
}

function buildMultiFileRetryInstruction(
  fieldKey: string,
  champion: StructuredOption,
  renderHistory: Array<{ rendered?: string }>,
): string {
  const historyLines = renderHistory
    .map((h, i) => `    file ${i + 1}: rendered "${h.rendered ?? '(no render)'}"`)
    .join('\n');

  return (
    `MULTI-FILE RETRY — READ THIS FIRST AND OVERRIDE ALL OTHER INSTRUCTIONS:\n` +
    `This template is being generalized across multiple sample documents of the same type.\n` +
    `Extract ONLY this field. Return "options": [] for every other field.\n` +
    `\n` +
    `Field: ${fieldKey}\n` +
    `The anchor previously used, "${champion.header}", no longer reliably finds the right value across all sample documents. Render history on prior documents:\n` +
    `${historyLines}\n` +
    `Find a DIFFERENT anchor for this field — one that is genuinely present, verbatim, in THIS document's table lines below, and that describes a stable column/field label likely to appear the same way across other documents using this same template (not a value from this specific document's data).\n`
  );
}

async function reconcileAcrossFiles(args: {
  isFirstFile: boolean;
  fileIndex: number;
  rawFileResult: StructuredResult;
  callRender: UtilsApi['callRender'];
  createCallRender: UtilsApi['createCallRender'];
  detected: DetectedTable[];
  lines: string[];
  domain: Domain;
  extractWithLLM: UtilsApi['extractWithLLM'];
  fieldsSection: string;
  textContent: string;
}): Promise<void> {
  const {
    isFirstFile,
    fileIndex,
    rawFileResult,
    callRender,
    createCallRender,
    detected,
    lines,
    domain,
    extractWithLLM,
    fieldsSection,
    textContent,
  } = args;

  const seedScores = (result: StructuredResult): Record<string, boolean> =>
    Object.fromEntries(
      Object.entries(result).map(([k, v]) => [k, (v.options?.length ?? 0) > 0]),
    );

  if (isFirstFile || !cachedBaseTemplate) {
    cachedBaseTemplate = JSON.parse(JSON.stringify(rawFileResult));
    cachedFileTexts.push(textContent);
    cachedPerFileScores[fileIndex] = seedScores(rawFileResult);
    return;
  }

  cachedFileTexts.push(textContent);
  const previousFileTexts = cachedFileTexts.slice(0, -1);

  const fieldKeys = new Set([
    ...Object.keys(cachedBaseTemplate),
    ...Object.keys(rawFileResult),
  ]);

  const scores: Record<string, boolean> = {};

  for (const fieldKey of fieldKeys) {
    const championRaw = selectBestOptions(fieldKey, cachedBaseTemplate[fieldKey]?.options ?? [])[0];
    const challengerRaw = selectBestOptions(fieldKey, rawFileResult[fieldKey]?.options ?? [])[0];
    const rawOptionsForField = rawFileResult[fieldKey]?.options;

    if (!championRaw && !challengerRaw) continue;

    if (!championRaw) {
      cachedBaseTemplate[fieldKey] = { options: [challengerRaw] };
      scores[fieldKey] = true;
      continue;
    }

    if (!challengerRaw) {
      const championResult = await scoreOption(fieldKey, championRaw, callRender);
      const retagged = retagTableIndex(championResult.opt, rawOptionsForField);
      cachedBaseTemplate[fieldKey] = {
        options: [{ ...retagged, value: championResult.rendered ?? retagged.value, verified: championResult.passes }],
      };
      scores[fieldKey] = championResult.passes;
      continue;
    }

    const championResult = await scoreOption(fieldKey, championRaw, callRender);
    const challengerResult = await scoreOption(fieldKey, challengerRaw, callRender);

    if (championResult.passes && challengerResult.passes) {
      const retagged = retagTableIndex(championResult.opt, rawOptionsForField);
      cachedBaseTemplate[fieldKey] = {
        options: [{ ...retagged, value: championResult.rendered ?? retagged.value, verified: true }],
      };
      scores[fieldKey] = true;
      continue;
    }

    if (!championResult.passes && !challengerResult.passes) {
      const history = await Promise.all(
        previousFileTexts.map(async (pastText) => {
          const pastRender = createCallRender(pastText);
          const r = await scoreOption(fieldKey, championRaw, pastRender);
          return { rendered: r.rendered };
        }),
      );

      const beforeCount = cachedStructuredResult?.[fieldKey]?.options?.length ?? 0;
      await retryFailingFields({
        failingFields: [{ key: fieldKey, value: championRaw.value, reason: 'renderMismatch' }],
        detected,
        lines,
        domain,
        extractWithLLM,
        fieldsSection,
        retryInstruction: buildMultiFileRetryInstruction(fieldKey, championRaw, history),
      });

      const afterOptions = cachedStructuredResult?.[fieldKey]?.options ?? [];
      const newOptions = afterOptions.slice(beforeCount);
      const retriedCandidate = selectBestOptions(fieldKey, newOptions)[0];

      if (retriedCandidate) {
        const retriedResult = await scoreOption(fieldKey, retriedCandidate, callRender);
        if (retriedResult.passes) {
          const retagged = retagTableIndex(retriedResult.opt, rawOptionsForField);
          cachedBaseTemplate[fieldKey] = {
            options: [{ ...retagged, value: retriedResult.rendered ?? retagged.value, verified: true }],
          };
          scores[fieldKey] = true;
          continue;
        }
      }

      scores[fieldKey] = false;
      continue;
    }

    let championPassCount = championResult.passes ? 1 : 0;
    let challengerPassCount = challengerResult.passes ? 1 : 0;

    for (const pastText of previousFileTexts) {
      const pastRender = createCallRender(pastText);
      const [pastChampion, pastChallenger] = await Promise.all([
        scoreOption(fieldKey, championRaw, pastRender),
        scoreOption(fieldKey, challengerRaw, pastRender),
      ]);
      if (pastChampion.passes) championPassCount++;
      if (pastChallenger.passes) challengerPassCount++;
    }

    let winnerIsChallenger: boolean;
    if (challengerPassCount > championPassCount) {
      winnerIsChallenger = true;
    } else if (challengerPassCount < championPassCount) {
      winnerIsChallenger = false;
    } else {
      winnerIsChallenger = challengerResult.passes && !championResult.passes;
    }

    const winnerResult = winnerIsChallenger ? challengerResult : championResult;
    const winnerRaw = winnerIsChallenger ? challengerRaw : championRaw;
    const retagged = retagTableIndex(winnerResult.opt ?? winnerRaw, rawOptionsForField);
    cachedBaseTemplate[fieldKey] = {
      options: [{ ...retagged, value: winnerResult.rendered ?? retagged.value, verified: winnerResult.passes }],
    };
    scores[fieldKey] = winnerResult.passes;
  }

  cachedPerFileScores[fileIndex] = scores;
  cachedStructuredResult = JSON.parse(JSON.stringify(cachedBaseTemplate));

}

function mergeExtractedResult(extracted: TableExtractResponse, tableIndex: number) {
  for (const [key, data] of Object.entries(extracted)) {
    mergeExtractedOptions(key, (data?.options ?? []) as any[], tableIndex);
  }
}

function mergeExtractedOptions(key: string, opts: any[], tableIndex: number) {
  mergeOptions(key, normalizeExtractedOptions(opts, tableIndex));
}

function cleanHeaderForColumn(header: string, col: number): string {
  const tokens = header
    .split(/\s{2,}/)
    .map((t) => t.trim())
    .filter(Boolean);
  if (tokens.length <= 1) return header.trim();
  return tokens[col] ?? header.trim();
}

function normalizeExtractedOptions(
  opts: any[],
  tableIndex: number,
): StructuredOption[] {
  return opts
    .map((o) => {
      const col = typeof o.col === 'number' ? o.col : 0;
      return {
        value: String(o.value ?? '').trim(),
        row: typeof o.row === 'number' ? o.row : 0,
        col,
        header: cleanHeaderForColumn(String(o.anchor ?? '').trim(), col),
        confidence: o.confidence,
        repeating: o.repeating === true,
        tableIndex,
      };
    })
    .filter((x) => x.value.length > 0);
}

function mergeOptions(key: string, options: StructuredOption[]) {
  if (options.length === 0) {
    return;
  }

  cachedStructuredResult = cachedStructuredResult || {};
  const normalizedKey = key.toLowerCase();
  cachedStructuredResult[normalizedKey] = {
    ...cachedStructuredResult[normalizedKey],
    options: [
      ...(cachedStructuredResult[normalizedKey]?.options ?? []),
      ...options,
    ],
  };
}


const SECTION_TABLE_INDEX = 1000;
const SECTION_SAMPLE_SIZE = 3;

export function countOccurrenceAnchors(iteratorValue: unknown): string[] | null {
  if (iteratorValue === null || typeof iteratorValue !== 'object') return null;
  const raw = (iteratorValue as Record<string, unknown>)['countoccurences'];
  const anchors = (Array.isArray(raw) ? raw : [raw]).filter(
    (a): a is string => typeof a === 'string' && a.trim().length > 0,
  );
  return anchors.length > 0 ? anchors : null;
}

export function splitIntoSections(lines: string[], anchors: string[]): string[][] {
  const starts = lines
    .map((line, i) => (anchors.some((a) => line.includes(a)) ? i : -1))
    .filter((i) => i !== -1);

  return starts.map((start, i) => lines.slice(start, starts[i + 1] ?? lines.length));
}

export function buildSectionSample(sections: string[][], size = SECTION_SAMPLE_SIZE): string[] {
  return sections
    .filter((section) => section.some((line) => line.trim()))
    .slice(0, size)
    .flatMap((section, i) => [`--- SECTION ${i + 1} ---`, ...section]);
}

export function locateColumn(
  section: string[],
  anchor: string,
  row: number,
  expected: string,
): number | null {
  const anchorLine = section.findIndex((line) => line.includes(anchor));
  const line = anchorLine === -1 ? undefined : section[anchorLine + row];
  if (line === undefined) return null;

  const columns = line.split(/\s{2,}/).map((cell) => cell.trim()).filter(Boolean);
  const col = columns.findIndex((cell) => valueMatchesRendered(expected, cell));
  return col === -1 ? null : col;
}

function sectionColumns(line: string): string[] {
  return line.split(/\s{2,}/).map((cell) => cell.trim()).filter(Boolean);
}

export function isIteratorAnchor(iterator: ChosenIterator, anchor: unknown): boolean {
  if (typeof anchor !== 'string' || !anchor.trim()) return false;
  return (countOccurrenceAnchors(iterator.value) ?? []).some(
    (a) => a.trim() === anchor.trim(),
  );
}

export function labelPrecedesValue(
  section: string[],
  anchor: string,
  row: number,
  col: number,
): boolean {
  if (row !== 0) return true;
  const line = section.find((l) => l.includes(anchor.trim()));
  if (line === undefined) return true;
  const labelCol = sectionColumns(line).findIndex((cell) => cell.includes(anchor.trim()));
  return labelCol === -1 || labelCol <= col;
}

export function sectionStartCandidates(
  section: string[],
  iteratorAnchors: string[],
  values: string[],
): Array<{ header: string; row: number; col: number; value: string }> {
  return iteratorAnchors.flatMap((anchor) =>
    values.flatMap((value) => {
      const col = locateColumn(section, anchor, 0, value);
      return col === null ? [] : [{ header: anchor, row: 0, col, value }];
    }),
  );
}

function candidateKey(opt: { header: string; row: number; col: number }): string {
  return `${opt.header.trim()}|${opt.row}|${opt.col}`;
}

export function buildSectionInstruction(fieldKeys: string[], sectionCount: number): string {
  return [
    `The text below shows example sections of a record that repeats ${sectionCount} times in the document (one section per iteration, marked "--- SECTION n ---").`,
    'The template evaluates every field inside ONE section at a time. The column header row of the page is NOT part of a section.',
    'For each field, use as "anchor" a literal label that appears in EVERY section (for example a prefix like "LA:" or "LO:"), with row and col relative to that anchor inside the section.',
    'Never use a column header, the "--- SECTION n ---" markers, or text that appears in only one section as the anchor. The value must be the one from SECTION 1.',
    `Only return these fields: ${fieldKeys.join(', ')}.`,
  ].join('\n');
}

async function extractFromIteratorSections(args: {
  lines: string[];
  domain: Domain;
  extractWithLLM: UtilsApi['extractWithLLM'];
  fieldsSection: string;
  callRender: UtilsApi['callRender'];
}) {
  const { lines, domain, extractWithLLM, fieldsSection, callRender } = args;
  if (!scoringIterator || !cachedStructuredResult) return;

  const anchors = countOccurrenceAnchors(scoringIterator.value);
  if (!anchors) {
    return;
  }

  const targetKeys = [...fieldPaths.keys()].filter(
    (key) =>
      iteratorForField(key) !== null &&
      (cachedStructuredResult?.[key]?.options?.length ?? 0) === 0,
  );
  const sections = splitIntoSections(lines, anchors);
  if (targetKeys.length === 0 || sections.length === 0) return;

  let extracted: TableExtractResponse;
  try {
    extracted = await extractFieldsFromTableWithLLM({
      tableLines: buildSectionSample(sections),
      promptName: getPromptName(domain),
      extractWithLLM,
      retryInstruction: buildSectionInstruction(targetKeys, sections.length),
      fieldsSection,
    });
  } catch {
    return;
  }

  const extractedByKey = new Map(
    Object.entries(extracted).map(([key, data]) => [key.toLowerCase(), data]),
  );

  await Promise.all(
    targetKeys.map(async (fieldKey) => {
      const llmCandidates = normalizeExtractedOptions(
        (extractedByKey.get(fieldKey)?.options ?? []) as any[],
        SECTION_TABLE_INDEX,
      )
        .map((opt) => ({ ...opt, repeating: true }))
        .flatMap((opt) => {
          const col = locateColumn(sections[0], opt.header, opt.row, opt.value);
          return col === null || col === opt.col ? [opt] : [{ ...opt, col }, opt];
        });

      const startCandidates = sectionStartCandidates(
        sections[0],
        anchors,
        [...new Set(llmCandidates.map((opt) => opt.value))],
      ).map((c) => ({
        ...c,
        confidence: undefined,
        repeating: true,
        tableIndex: SECTION_TABLE_INDEX,
      }));

      const candidates = dedupeByCell([...startCandidates, ...llmCandidates]);
      if (candidates.length === 0) return;

      const results = await Promise.all(
        candidates.map((opt) => scoreOption(fieldKey, opt, callRender)),
      );


      const passing = dedupeByCell(
        results
          .filter((r) => r.passes)
          .map((r) => ({
            ...r.opt,
            value: r.rendered ?? r.opt.value,
            expectedValue: r.opt.value,
            verified: true,
          })),
      );
      if (passing.length > 0) {
        cachedStructuredResult![fieldKey] = { options: passing };
      }
    }),
  );
}

async function processDetectedTables(args: {
  lines: string[];
  domain: Domain;
  detected: DetectedTable[];
  extractWithLLM: UtilsApi['extractWithLLM'];
  fieldsSection: string;
}) {
  const { lines, domain, detected, extractWithLLM, fieldsSection } = args;
  const promptName = getPromptName(domain);

  const allResults = await Promise.all(
    detected.map((t) => {
      const tableLines = lines.slice(t.startLine, t.endLine + 1);
      if (tableLines.length === 0) {
        return Promise.resolve({} as TableExtractResponse);
      }
      return extractFieldsFromTableWithLLM({
        tableLines,
        promptName,
        extractWithLLM,
        fieldsSection,
      });
    }),
  );

  cachedStructuredResult = cachedStructuredResult || {};
  allResults.forEach((extracted, tableIndex) => {
    mergeExtractedResult(extracted, tableIndex);
  });
}
